<script setup lang="ts">
import { computed } from 'vue';

import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Dialog from 'primevue/dialog';

import { useI18n } from 'vue-i18n';

import { useDeleteConfirmStore } from '@/stores/deleteConfirm';

const store = useDeleteConfirmStore();
const { t } = useI18n();

const visible = computed({
  get: () => store.visible,
  set: (value: boolean) => {
    if (!value) {
      store.close();
    }
  },
});

const blockingItems = computed(() => store.options?.blockingItems ?? []);
const blocked = computed(() => blockingItems.value.length > 0);

const header = computed(() =>
  blocked.value ? t('common.cannot_delete') : (store.options?.header ?? t('common.confirm_delete')),
);
const acceptLabel = computed(() => store.options?.acceptLabel ?? t('common.delete'));
const rejectLabel = computed(() =>
  blocked.value ? t('common.close') : (store.options?.rejectLabel ?? t('common.cancel')),
);
const acknowledgmentLabel = computed(() => store.options?.acknowledgmentLabel ?? t('common.delete_acknowledgment'));

const message = computed(() => {
  if (store.options?.message) {
    return store.options.message;
  }
  if (store.options?.itemName) {
    return t('common.delete_message_named', { name: store.options.itemName });
  }
  return t('common.delete_message_generic');
});

const onCancel = () => store.close();
const onConfirm = () => store.confirm();
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="header"
    modal
    :closable="!store.submitting"
    :draggable="false"
    :style="{ width: '480px' }"
    data-testid="delete-confirm-dialog"
  >
    <div class="flex flex-col gap-4">
      <div class="flex items-start gap-3">
        <i class="pi pi-exclamation-triangle text-2xl text-red-500"></i>
        <p class="text-surface-700 dark:text-surface-200 text-sm leading-relaxed">
          {{ blocked ? store.options?.blockedMessage : message }}
        </p>
      </div>

      <div v-if="blocked" class="flex flex-col gap-2" data-testid="delete-confirm-blocked">
        <ul
          class="max-h-48 overflow-y-auto rounded-md border border-gray-200 text-sm dark:border-gray-700"
          data-testid="delete-confirm-blocking-items"
        >
          <li
            v-for="item in blockingItems"
            :key="item.label"
            class="border-b border-gray-200 px-3 py-2 last:border-b-0 dark:border-gray-700"
          >
            <router-link :to="item.to" class="text-gray-900 hover:underline dark:text-gray-100" @click="onCancel">
              {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <label
        v-else
        class="border-surface-200 hover:bg-surface-50 dark:border-surface-700 dark:hover:bg-surface-800 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
        data-testid="delete-confirm-acknowledgment"
      >
        <Checkbox v-model="store.acknowledged" :binary="true" :disabled="store.submitting" />
        <span class="text-surface-700 dark:text-surface-200 text-sm">{{ acknowledgmentLabel }}</span>
      </label>
    </div>

    <template #footer>
      <Button
        :label="rejectLabel"
        severity="secondary"
        text
        :disabled="store.submitting"
        @click="onCancel"
        data-testid="delete-confirm-cancel"
      />
      <Button
        v-if="!blocked"
        :label="acceptLabel"
        severity="danger"
        icon="pi pi-trash"
        :loading="store.submitting"
        :disabled="!store.acknowledged || store.submitting"
        @click="onConfirm"
        data-testid="delete-confirm-accept"
      />
    </template>
  </Dialog>
</template>
